/**
 * Canonical Schedule call-time cues. Stored as kind:'schedule' rows in the
 * same JSON document as the day list so header edits stay in sync.
 */
import {
  formatTimeInputMask,
  parseScheduleTimeFields,
} from './scheduleTimeUtils';

export const SCHEDULE_CUES = [
  { id: 'doors', label: 'Doors', item: 'DOORS', aliases: ['doors', 'door'] },
  {
    id: 'set',
    label: 'Set',
    item: 'SET',
    aliases: ['set time', 'set', 'downbeat', 'show time'],
  },
  {
    id: 'venueCurfew',
    label: 'Venue Curfew',
    item: 'VENUE CURFEW',
    aliases: ['venue curfew'],
  },
  {
    id: 'grCurfew',
    label: 'GR Curfew',
    item: 'GR CURFEW',
    aliases: ['gr curfew', 'green room curfew', 'guest curfew'],
  },
  {
    id: 'guestList',
    label: 'Guest List',
    item: 'GUEST LIST',
    aliases: ['guest list'],
  },
  {
    id: 'busCall',
    label: 'Bus Call',
    item: 'BUS CALL',
    aliases: ['bus call'],
  },
];

/** Production Day Sheet template. Shared with Schedule so empty days land both places. */
export const DAY_SHEET_DEFAULT_ITEMS = [
  'LOBBY CALL',
  'VENUE ACCESS',
  'LOAD IN',
  'SUPPORT LOAD IN',
  'LINECHECK ARTIST 1',
  'SOUNDCHECK ARTIST 1',
  'SOUNDCHECK ARTIST 2',
  'SOUNDCHECK ARTIST 3',
  'DOORS',
  'ARTIST 3',
  'ARTIST 2',
  'ARTIST 1',
  'VENUE CURFEW',
  'BUS CALL',
];

function cueRow(cue, { time = '', period = 'AM' } = {}) {
  return {
    id: `cue_${cue.id}`,
    kind: 'schedule',
    time,
    period,
    item: cue.item,
    notes: '',
  };
}

function defaultSeedRows() {
  const extrasBefore = {
    'VENUE CURFEW': ['grCurfew'],
  };
  const extrasAfter = {
    DOORS: ['set', 'guestList'],
  };
  const rows = [];
  DAY_SHEET_DEFAULT_ITEMS.forEach((label) => {
    (extrasBefore[label] || []).forEach((cueId) => {
      const cue = cueById(cueId);
      if (cue) rows.push(cueRow(cue));
    });
    rows.push({
      id: `default_${label.toLowerCase().replace(/[^\w]+/g, '_')}`,
      kind: 'schedule',
      time: '',
      period: 'AM',
      item: label,
      notes: '',
    });
    (extrasAfter[label] || []).forEach((cueId) => {
      const cue = cueById(cueId);
      if (cue) rows.push(cueRow(cue));
    });
  });
  SCHEDULE_CUES.forEach((cue) => {
    if (findCueLineIndex(rows, cue.id) < 0) rows.push(cueRow(cue));
  });
  return rows;
}

function newLineId() {
  return `l_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function parseLines(raw) {
  const s = String(raw || '').trim();
  if (!s) return [];
  try {
    const o = JSON.parse(s);
    if (o && o.v === 1 && Array.isArray(o.lines)) return o.lines.slice();
  } catch {
    /* fall through */
  }
  return [{ id: newLineId(), kind: 'text', text: String(raw) }];
}

function serializeLines(lines) {
  const list = Array.isArray(lines) ? lines : [];
  if (!list.length) return '';
  return JSON.stringify({ v: 1, lines: list });
}

function itemKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function cueById(cueId) {
  return SCHEDULE_CUES.find((c) => c.id === cueId) || null;
}

function matchesCue(item, cue) {
  const key = itemKey(item);
  if (!key) return false;
  if (cue.id === 'venueCurfew') {
    if (key.includes('gr curfew') || key.includes('green room') || key === 'guest curfew') {
      return false;
    }
    if (key === 'venue curfew' || key === 'curfew') return true;
    return cue.aliases.some((a) => key === a || key.includes(a));
  }
  if (cue.id === 'set') {
    if (key.includes('setlist') || key.includes('guest list')) return false;
    return cue.aliases.some((a) => key === a || (a !== 'set' && key.includes(a)))
      || key === 'set';
  }
  return cue.aliases.some((a) => key === a || key.includes(a));
}

export function findCueLineIndex(lines, cueId) {
  const cue = cueById(cueId);
  if (!cue || !Array.isArray(lines)) return -1;
  return lines.findIndex((ln) => ln && ln.kind === 'schedule' && matchesCue(ln.item, cue));
}

export function getScheduleCue(raw, cueId) {
  const lines = parseLines(raw);
  const idx = findCueLineIndex(lines, cueId);
  if (idx < 0) return { time: '', period: 'AM' };
  const parsed = parseScheduleTimeFields(lines[idx].time, lines[idx].period);
  return { time: parsed.time, period: parsed.period };
}

export function upsertScheduleCue(raw, cueId, { time = '', period = 'AM' } = {}) {
  const cue = cueById(cueId);
  if (!cue) return String(raw || '');
  const parsed = parseScheduleTimeFields(time, period);
  let lines = parseLines(raw);
  const idx = findCueLineIndex(lines, cueId);
  const row = {
    id: idx >= 0 ? lines[idx].id : newLineId(),
    kind: 'schedule',
    time: formatTimeInputMask(parsed.time),
    period: parsed.period,
    item: cue.item,
    notes: idx >= 0 ? String(lines[idx].notes || '') : '',
  };
  if (idx >= 0) {
    lines[idx] = { ...lines[idx], ...row };
  } else {
    lines = [...lines, row];
  }
  return serializeLines(lines);
}

/** Empty docs get Day Sheet production rows plus the six header cues. */
export function seedScheduleCues(raw, { busCallTime = '' } = {}) {
  const lines = parseLines(raw);
  const hasSchedule = lines.some((ln) => ln && ln.kind === 'schedule');
  let next = hasSchedule ? lines : defaultSeedRows();
  if (!hasSchedule && lines.some((ln) => ln && ln.kind === 'text' && String(ln.text || '').trim())) {
    next = [...next, ...lines];
  }
  const seeded = serializeLines(next);
  return stripScheduleSettlementRows(applyBusCallPrefill(seeded, busCallTime));
}

function isSettlementScheduleItem(item) {
  const text = String(item || '').toLowerCase();
  return text.includes('per diem') || text.includes('settlement');
}

/** Per diems and settlement stay on Expenses. They are not schedule rows. */
export function stripScheduleSettlementRows(raw) {
  const lines = parseLines(raw);
  const next = lines.filter((line) => !(line && line.kind === 'schedule' && isSettlementScheduleItem(line.item)));
  if (next.length === lines.length) return String(raw || '');
  return serializeLines(next);
}

export function applyBusCallPrefill(raw, busCallTime) {
  const parsed = parseScheduleTimeFields(busCallTime);
  if (!parsed.time) return String(raw || '');
  const current = getScheduleCue(raw, 'busCall');
  if (current.time) return String(raw || '');
  return upsertScheduleCue(raw, 'busCall', { time: parsed.time, period: parsed.period });
}
